<template lang="pug">
  .cms-tabs
    Tabs.tab-select(
      @update:modelValue="changeTab($event)"
      :class="theme"
      :title="title"
      :theme="theme"
      :topHeading="topHeading"
      :modelValue="show"
      :tabs="tabs.map(t => t.props.tab)"
      :desktopScroll="desktopScroll"
    )
    .tab-content-container
      .cms-tab(
        v-for="tab in tabs"
        :key="tab.props.tab"
        :class="show !== tab.props.tab && 'inactive'"
        :aria-label="'Tab Contents: ' + tab.props.tab"
        :data-tab="tab.props.tab"
        tabindex="-1"
      )
        .tab-content(tabindex="-1")
          RenderBlock(:block="tab" :product="product")
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRogue } from '../../js/rogue'

type Tab = {
  props: {
    tab: string,
    showIf?: {key: string, value: any}[],
  },
}

const props = withDefaults(defineProps<{
  active?: number,
  title?: string | null,
  theme?: string | null,
  hideInactive?: boolean,
  tabs?: Tab[],
  product?: object,
  rhpaConfig?: object | null,
  topHeading?: boolean,
  desktopScroll?: boolean,
}>(), {
  active        : 0,
  title         : null,
  theme         : null,
  hideInactive  : false,
  tabs          : () => [],
  product       : () => ({}),
  rhpaConfig    : null,
  topHeading    : false,
  desktopScroll : false,
})

const rogue = useRogue()

const show = ref<string>(props.tabs[props.active].props.tab)

const displayedTabs = computed(() => props.tabs.filter(tab => show.value === tab.props.tab || props.hideInactive !== true))

// If the RHPA changes, we may want to change which tab is showing
watch(() => props.rhpaConfig, (val) => {
  if (val) {
    // The children of this component (the tabs) are components themselves, with properties.
    // The `showIf` property can be used to specify an array of conditions to match against the RHPA config
    // to determine if this tab should be shown or not.
    const changeTo = props.tabs
      .filter(tab => (tab.props.showIf || []).length)
      .find(tab => tab.props.showIf?.every(cond => val[cond.key] === cond.value))

    if (changeTo) {
      show.value = changeTo.props.tab
    }
  }
}, {
  deep      : true,
  immediate : true,
})

// We have to do this on mounted because the SSR does not see the hashes
onMounted(() => {
  const hash = (rogue?.router?.currentRoute.value?.hash || '').slice(1)
  // If the route has a tab selected, find it
  if (!hash) {
    return
  }

  const tab = props.tabs.find(t => t.props.tab.toLowerCase().replace(/[^a-z0-9]/g, '') === hash)

  if (tab) {
    show.value = tab.props.tab
  }
})

function changeTab(event: string) {
  // Dont change if already showing this tab
  if (show.value === event) {
    return
  }

  show.value = event

  if (rogue && rogue.track) {
    rogue.track.track({ type: 'changeTab', tab: event })
  }
  if (rogue?.router) {
    rogue.router.replace({ hash: `#${event.toLowerCase().replace(/[^a-z0-9]/g, '')}` })
  }
}
</script>
<style lang="scss" scoped>
@include mobile() {
  .tab-select {
    z-index: 2;

    :deep(.tab-title) {
      white-space: nowrap;
    }

    &:not(.cms):not(.theme-app):not(.theme-outline){
      padding-top: $space-xs;
    }
  }
}

.inactive {
  display: none;
}
</style>
